import type { Card } from '@klopotun/contracts';
import { BANK_OPERATION_KIND } from '@klopotun/domain';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  type BankOperationDetail,
  useBankOperationQuery,
} from '../../entities/bank-transaction/index.js';
import { useCardsQuery } from '../../entities/card/index.js';
import { useMeQuery } from '../../entities/me/index.js';
import { useOccurrenceStatusMutation } from '../../entities/occurrence/index.js';
import { paymentDetailPath } from '../../features/navigation/index.js';
import { formatStoredDateForPeople } from '../../features/payment-view/index.js';
import { formatApproximate, formatSignedMoney } from '../../features/summary-view/index.js';
import { useBackButton } from '../../shared/telegram/index.js';
import {
  Button,
  ErrorState,
  List,
  ListItem,
  PageHeader,
  Screen,
  Sheet,
  type SheetAction,
  Spinner,
} from '../../shared/ui/index.js';
import {
  convertedAmountMinorOf,
  operationDescriptionOf,
  operationKindTextOf,
} from '../../widgets/bank-operations/index.js';
import { SummaryNote } from '../../widgets/year-summary/index.js';
import { LinkSubscriptionSheet } from './link-subscription-sheet/link-subscription-sheet.js';
import { OperationCategory } from './operation-category/operation-category.js';
import styles from './operation-detail-page.module.css';
import { OperationSubscription } from './operation-subscription/operation-subscription.js';

const PENDING_VALUE = '…';

const FAILED_VALUE = 'не завантажилось';

const UNCOUNTED_TEXT = 'не враховано';

type CardFact = {
  cards: Card[] | undefined;
  isPending: boolean;
  isFailed: boolean;
};

const cardTextOf = (cardId: string | null, fact: CardFact): string => {
  if (cardId === null) {
    return 'без картки';
  }

  if (fact.isPending) {
    return PENDING_VALUE;
  }

  if (fact.isFailed) {
    return FAILED_VALUE;
  }

  return fact.cards?.find((card) => card.id === cardId)?.label ?? 'без картки';
};

const convertedTextOf = (
  operation: BankOperationDetail,
  baseCurrency: string,
): string | undefined => {
  const convertedMinor = convertedAmountMinorOf(operation, baseCurrency);

  if (convertedMinor !== null) {
    return formatApproximate(formatSignedMoney(convertedMinor, baseCurrency));
  }

  const isForeign = operation.currency !== baseCurrency;
  const isUncounted = isForeign && operation.subscription === null;

  return isUncounted ? UNCOUNTED_TEXT : undefined;
};

const kindHintOf = (operation: BankOperationDetail): string | null => {
  const isIncome = operation.kind === BANK_OPERATION_KIND.INCOME;
  const isOutsideSpending = isIncome || operation.kind === BANK_OPERATION_KIND.TRANSFER;

  if (isOutsideSpending) {
    return 'Надходження й перекази між своїми рахунками не мають категорії й не входять у витрати.';
  }

  if (operation.kind === BANK_OPERATION_KIND.REFUND) {
    return 'Повернення зменшує витрати своєї категорії.';
  }

  return null;
};

const hasCategoryChoice = (operation: BankOperationDetail): boolean => {
  const isSpending =
    operation.kind === BANK_OPERATION_KIND.EXPENSE || operation.kind === BANK_OPERATION_KIND.REFUND;

  return isSpending && operation.subscription === null;
};

const CenteredSpinner = () => (
  <Screen>
    <div className={styles.centered}>
      <Spinner label="Завантаження" />
    </div>
  </Screen>
);

export const OperationDetailPage = () => {
  const [isLinkOpen, setLinkOpen] = useState(false);
  const [isUnmatchAsked, setUnmatchAsked] = useState(false);
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const me = useMeQuery();
  const operation = useBankOperationQuery(id);
  const cards = useCardsQuery({ includeArchived: true });
  const unmatch = useOccurrenceStatusMutation();
  const goBack = useCallback(() => {
    if (isUnmatchAsked) {
      setUnmatchAsked(false);

      return;
    }

    if (isLinkOpen) {
      setLinkOpen(false);

      return;
    }

    void navigate(-1);
  }, [isUnmatchAsked, isLinkOpen, navigate]);
  const closeLink = useCallback(() => {
    setLinkOpen(false);
  }, []);

  useBackButton(goBack);

  if (operation.isPending || me.isPending) {
    return <CenteredSpinner />;
  }

  if (operation.isError) {
    return (
      <Screen>
        <ErrorState
          title="Не вдалося завантажити операцію"
          message={operation.error.message}
          onRetry={() => {
            void operation.refetch();
          }}
        />
      </Screen>
    );
  }

  if (me.isError) {
    return (
      <Screen>
        <ErrorState
          title="Не вдалося завантажити операцію"
          message={me.error.message}
          onRetry={() => {
            void me.refetch();
          }}
        />
      </Screen>
    );
  }

  const loaded = operation.data;
  const baseCurrency = me.data.settings.baseCurrency;
  const kindHint = kindHintOf(loaded);
  const subscription = loaded.subscription;
  const canLink = loaded.isLinkable && subscription === null;

  const unmatchConfirmation: SheetAction[] =
    subscription === null
      ? []
      : [
          {
            id: 'confirm-unmatch',
            label: 'Так, скасувати зіставлення',
            danger: true,
            disabled: unmatch.isPending,
            onSelect: () => {
              unmatch.mutate(
                { action: 'unpay', id: subscription.occurrenceId },
                {
                  onSettled: () => {
                    setUnmatchAsked(false);
                  },
                },
              );
            },
          },
        ];

  return (
    <Screen>
      <div className={styles.content}>
        <PageHeader title="Операція" />

        <List>
          <ListItem
            title={operationDescriptionOf(loaded)}
            isTitleWrapping
            subtitle={formatStoredDateForPeople(loaded.occurredOn)}
            value={formatSignedMoney(loaded.amountMinor, loaded.currency)}
            valueNote={convertedTextOf(loaded, baseCurrency)}
          />
        </List>

        <List header="Подробиці">
          <ListItem title="Вид" value={operationKindTextOf(loaded.kind)} />
          {loaded.purchase !== null && (
            <ListItem
              title="Сума покупки"
              value={formatSignedMoney(loaded.purchase.amountMinor, loaded.purchase.currency)}
            />
          )}
          <ListItem
            title="Картка"
            value={cardTextOf(loaded.cardId, {
              cards: cards.data,
              isPending: cards.isPending,
              isFailed: cards.isError,
            })}
          />
        </List>

        {kindHint !== null && <SummaryNote tone="hint">{kindHint}</SummaryNote>}

        {hasCategoryChoice(loaded) && <OperationCategory key={loaded.id} operation={loaded} />}

        {subscription !== null && (
          <OperationSubscription
            subscription={subscription}
            isUnmatching={unmatch.isPending}
            onOpen={() => {
              void navigate(paymentDetailPath(subscription.paymentId));
            }}
            onUnmatch={() => {
              unmatch.reset();
              setUnmatchAsked(true);
            }}
          />
        )}

        {unmatch.isError && <SummaryNote tone="warning">{unmatch.error.message}</SummaryNote>}

        {canLink && (
          <Button
            variant="secondary"
            onClick={() => {
              setLinkOpen(true);
            }}
          >
            Це оплата підписки…
          </Button>
        )}
      </div>

      {isLinkOpen && (
        <LinkSubscriptionSheet
          key={loaded.id}
          operationId={loaded.id}
          occurredOn={loaded.occurredOn}
          onClose={closeLink}
        />
      )}

      <Sheet
        open={isUnmatchAsked && subscription !== null}
        actions={unmatchConfirmation}
        title="Скасувати зіставлення? Платіж повернеться в план, а ця операція сама вже нічого не підтверджуватиме — прив'язати її знову можна тут, на екрані операції."
        closeLabel="Ні, лишити"
        onClose={() => {
          setUnmatchAsked(false);
        }}
      />
    </Screen>
  );
};
